function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import forEach from 'lodash/forEach';
import indexOf from 'lodash/indexOf';
import map from 'lodash/map';
import find from 'lodash/find';
import last from 'lodash/last';
import omit from 'lodash/omit';
import filter from 'lodash/filter';
import round from 'lodash/round';
import { calculateRelatedValue } from 'helpers/relatedData';
import { isManualReading } from 'helpers/externalDataSources';
import { isAggregatedPostMeal } from 'libs/StatsCalculations';
import ReadingFlagIcon from 'components/ReadingFlagIcon';
import ActivityIcon from 'svg/activity.svg';
import MealIcon from 'svg/meal.svg';
import InjectionIcon from 'svg/injection.svg';
import AverageIcon from 'svg/average-blood.svg';
import messages from '../../../messages';
import { TimeSeriesResourcesHelper } from '../../../helpers/TimeSeriesResourcesHelper';
import styles from './DailyChart.pcss';
var _React_PureComponent;
class DailyChart extends (_React_PureComponent = React.PureComponent) {
    static getDerivedStateFromProps(props) {
        const { readings, relatedData, timeSeriesResources } = props;
        const mergedRelatedDataWithReadings = readings.map((reading)=>{
            const findRelatedData = relatedData && relatedData.find((item)=>item.readingId === reading.externalId);
            if (findRelatedData) {
                reading.relatedData = findRelatedData;
            }
            return reading;
        });
        const translateMomentDate = (momentDate)=>+momentDate.utc().locale('en--account').startOf('day').format('X');
        const rowsIndex = [];
        const rows = {};
        forEach(mergedRelatedDataWithReadings, (reading)=>{
            const rowMomentDate = moment.unix(reading.timestamp).utc();
            const rowTimestamp = +translateMomentDate(rowMomentDate);
            if (indexOf(rowsIndex, rowTimestamp) < 0) {
                rowsIndex.push(rowTimestamp);
                rows[rowTimestamp] = [];
            }
            rows[rowTimestamp].push(reading);
        });
        const timeSeriesResourcesHelper = new TimeSeriesResourcesHelper();
        const translatedTimeSeriesResources = timeSeriesResourcesHelper.translateToDailyLogReportRelatedValues(timeSeriesResources);
        forEach(translatedTimeSeriesResources, (translatedTimeSeriesResource)=>{
            const rowMomentDate = moment.unix(translatedTimeSeriesResource.timestamp);
            const rowTimestamp = +translateMomentDate(rowMomentDate);
            if (indexOf(rowsIndex, rowTimestamp) < 0) {
                rowsIndex.push(rowTimestamp);
                rows[rowTimestamp] = [];
            }
            rows[rowTimestamp].push({
                timestamp: translatedTimeSeriesResource.timestamp,
                timeSeriesResources: translatedTimeSeriesResource
            });
        });
        return {
            rows
        };
    }
    renderFlag(reading, isManual) {
        const { flags } = reading;
        return /*#__PURE__*/ _jsx("div", {
            className: cn(styles.reading__flagIconContainer, {
                [styles['reading__flagIconContainer--manual']]: isManual
            }),
            children: /*#__PURE__*/ _jsx(ReadingFlagIcon, {
                flag: flags
            })
        });
    }
    renderRelatedData(values, left, name, reading) {
        return /*#__PURE__*/ _jsx("div", {
            style: {
                left: `${left}%`
            },
            className: styles.relatedData__valueWrapper,
            children: map(values, (value, index)=>{
                if (!value) {
                    return null;
                }
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles[index],
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.relatedData__valueWrapperTimeIndicator
                        }),
                        value
                    ]
                }, `related-data-at-${index}`);
            })
        }, `related-data-${name}-${reading.timestamp}-${reading.deviceSerialNumberToken}`);
    }
    renderReading(reading, left) {
        const { value } = reading;
        const { isPlainValuesMode } = this;
        const { conversion, standards } = this.props;
        const toDisplay = isPlainValuesMode ? value : this.props.conversion.toDisplay(value);
        const isManual = isManualReading(reading);
        const highConverted = isAggregatedPostMeal(reading.flags) ? conversion.toDisplay(standards.postMeal.highThreshold) : conversion.toDisplay(standards.preMeal.highThreshold);
        const lowConverted = isAggregatedPostMeal(reading.flags) ? conversion.toDisplay(standards.postMeal.lowThreshold) : conversion.toDisplay(standards.preMeal.lowThreshold);
        const isHigh = !isPlainValuesMode && toDisplay > highConverted;
        const isLow = !isPlainValuesMode && toDisplay < lowConverted;
        return /*#__PURE__*/ _jsxs("div", {
            style: {
                left: `${left}%`
            },
            className: cn(isManual ? styles.reading__valueManualWrapper : styles.reading__valueWrapper, {
                [styles['reading--high']]: isHigh,
                [styles['reading--low']]: isLow
            }),
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.relatedData__valueWrapperTimeIndicator
                }),
                this.renderFlag(reading, isManual),
                /*#__PURE__*/ _jsx("div", {
                    children: conversion.toDisplay(reading.value)
                })
            ]
        }, `reading-daily-${reading.timestamp}-${reading.deviceSerialNumberToken}`);
    }
    renderBreakPoints(timestamp) {
        if (!this.props.showDayDividers) {
            return null;
        }
        const start = timestamp;
        const end = +moment.unix(timestamp).utc().locale('en--account').add(1, 'days').format('X');
        const diff = end - start;
        const ticks = map(new Array(12), (value, index)=>moment.unix(timestamp).utc().locale('en--account').set({
                hour: index * 2,
                minute: 0
            }).unix());
        ticks.push(end);
        return /*#__PURE__*/ _jsx("div", {
            className: styles.breakPointsWrapper,
            children: ticks.map((tick)=>{
                const timeFromStart = tick - start;
                const percentageOnChart = (timeFromStart / diff * 100).toFixed(2);
                return /*#__PURE__*/ _jsxs("div", {
                    className: styles.breakPoint,
                    style: {
                        left: `${percentageOnChart}%`
                    },
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.timeLine
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: styles.timeText,
                            children: moment.unix(tick).utc().locale('en--account').format('HH:mm')
                        })
                    ]
                }, `tick-at-${tick}`);
            })
        });
    }
    renderSummary(row, timestamp, stackedReadingsRows) {
        const { conversion, hideEmptyRows } = this.props;
        let activityValues = 0;
        let foodValues = 0;
        let medicationFastValues = 0;
        let medicationLongValues = 0;
        let sumValue = 0;
        const rowWithReadings = filter(row, (item)=>!!item.readingId || isManualReading(item));
        row.forEach((item)=>{
            sumValue += item.value ? conversion.toDisplay(item.value) : 0;
            if (item.relatedData) {
                const { activityValue, foodValue, medicationFastValue, medicationLongValue } = calculateRelatedValue(item.relatedData);
                activityValues += activityValue;
                foodValues += foodValue;
                medicationFastValues += medicationFastValue;
                medicationLongValues += medicationLongValue;
            }
            if (item.timeSeriesResources) {
                const { activityValue, foodValue, medicationFastValue, medicationLongValue } = item.timeSeriesResources;
                activityValues += activityValue || 0;
                foodValues += foodValue || 0;
                medicationFastValues += medicationFastValue || 0;
                medicationLongValues += medicationLongValue || 0;
            }
        });
        const averageValue = rowWithReadings.length ? (sumValue / rowWithReadings.length).toFixed(conversion.unit === 'MG_DL' ? 0 : 2) : 0;
        const hideFoodSummary = hideEmptyRows && foodValues === 0;
        const hideMedicationsSummary = hideEmptyRows && medicationLongValues === 0 && medicationFastValues === 0;
        const hideActivitiesSummary = hideEmptyRows && activityValues === 0;
        const emptyDivsToRender = stackedReadingsRows.length - 1;
        const dateFormat = moment.localeData().longDateFormat('L').replace(/.?YYYY.?/, '');
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.summary,
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    children: moment.unix(timestamp).utc().format(`dddd, ${dateFormat}`)
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.average,
                    children: [
                        /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.dailyReport.average)),
                        ` ${averageValue} ${conversion.unitSymbol}`
                    ]
                }),
                emptyDivsToRender > 0 && map(Array(emptyDivsToRender).keys(), ()=>/*#__PURE__*/ _jsx("div", {
                        style: {
                            visibility: 'hidden',
                            height: '20px'
                        }
                    })),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.relatedData__valueWrapper,
                    style: {
                        display: hideFoodSummary ? 'none' : 'flex'
                    },
                    children: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            foodValues,
                            " ",
                            /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.foodUnit))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.relatedData__valueWrapper,
                    style: {
                        display: hideMedicationsSummary ? 'none' : 'flex'
                    },
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                round(medicationFastValues, 2),
                                " ",
                                /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.injectionUnit))
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                round(medicationLongValues, 2),
                                " ",
                                /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.injectionUnit))
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.relatedData__valueWrapper,
                    style: {
                        display: hideActivitiesSummary ? 'none' : 'flex'
                    },
                    children: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            activityValues,
                            " ",
                            /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.labels.activityUnit))
                        ]
                    })
                })
            ]
        });
    }
    renderRelatedDataRowIcon(type) {
        let icon;
        switch(type){
            case 'foods':
                icon = /*#__PURE__*/ _jsx(MealIcon, {});
                break;
            case 'medications':
                icon = /*#__PURE__*/ _jsx(InjectionIcon, {});
                break;
            case 'activities':
                icon = /*#__PURE__*/ _jsx(ActivityIcon, {});
                break;
            default:
                icon = null;
        }
        return /*#__PURE__*/ _jsx("div", {
            className: styles.row__icon,
            children: icon
        });
    }
    getRelatedDataRowValue(row, type) {
        let { activityValue, foodValue, medicationFastValue, medicationLongValue } = calculateRelatedValue(row.relatedData || {});
        if (row.timeSeriesResources) {
            const { activityValue: tsrActivityValue, foodValue: tsrFoodValue, medicationFastValue: tsrMedicationFastValue, medicationLongValue: tsrMedicationLongValue } = row.timeSeriesResources;
            activityValue += tsrActivityValue || 0;
            foodValue += tsrFoodValue || 0;
            medicationFastValue += tsrMedicationFastValue || 0;
            medicationLongValue += tsrMedicationLongValue || 0;
        }
        let result = {};
        switch(type){
            case 'foods':
                if (foodValue) {
                    result.foodValue = foodValue;
                }
                break;
            case 'medications':
                if (medicationFastValue) {
                    result.medicationFastValue = medicationFastValue;
                }
                if (medicationLongValue) {
                    result.medicationLongValue = medicationLongValue;
                }
                break;
            case 'activities':
                if (activityValue) {
                    result.activities = activityValue;
                }
                break;
            default:
                result = null;
        }
        return result;
    }
    renderRelatedDataRow(row, timestamp, type) {
        const { hideEmptyRows } = this.props;
        let activityValues = 0;
        let foodValues = 0;
        let medicationFastValues = 0;
        let medicationLongValues = 0;
        row.forEach((item)=>{
            if (item.relatedData) {
                const { activityValue, foodValue, medicationFastValue, medicationLongValue } = calculateRelatedValue(item.relatedData);
                activityValues += activityValue;
                foodValues += foodValue;
                medicationFastValues += medicationFastValue;
                medicationLongValues += medicationLongValue;
            }
            if (item.timeSeriesResources) {
                const { activityValue, foodValue, medicationFastValue, medicationLongValue } = item.timeSeriesResources;
                activityValues += activityValue || 0;
                foodValues += foodValue || 0;
                medicationFastValues += medicationFastValue || 0;
                medicationLongValues += medicationLongValue || 0;
            }
        });
        const hideFood = hideEmptyRows && foodValues === 0;
        const hideMedications = hideEmptyRows && medicationLongValues === 0 && medicationFastValues === 0;
        const hideActivities = hideEmptyRows && activityValues === 0;
        if (hideEmptyRows && hideFood && type === 'foods') {
            return null;
        }
        if (hideEmptyRows && hideMedications && type === 'medications') {
            return null;
        }
        if (hideEmptyRows && hideActivities && type === 'activities') {
            return null;
        }
        const start = timestamp;
        const end = +moment.unix(timestamp).utc().locale('en').add(1, 'days').format('X');
        const diff = end - start;
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.row__wrapper,
            children: [
                this.renderRelatedDataRowIcon(type),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.row,
                    children: map(row, (data)=>{
                        const timeFromStart = data.timestamp - start;
                        const percentageOnChart = (timeFromStart / diff * 100).toFixed(2);
                        if (data.relatedData || data.timeSeriesResources) {
                            const relatedDataValue = this.getRelatedDataRowValue(data, type);
                            if (!relatedDataValue || !Object.keys(relatedDataValue).length) {
                                return null;
                            }
                            return this.renderRelatedData(relatedDataValue, percentageOnChart, type, data);
                        }
                        return null;
                    })
                })
            ]
        });
    }
    renderEmptyReadingsRow() {
        const { hideEmptyRows } = this.props;
        if (hideEmptyRows) return null;
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.row__wrapper,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.row__icon,
                    children: /*#__PURE__*/ _jsx(AverageIcon, {})
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.row
                })
            ]
        });
    }
    renderDay(row, timestamp) {
        const { hideEmptyRows } = this.props;
        if (hideEmptyRows && !row.length) {
            return null;
        }
        const start = timestamp;
        const end = +moment.unix(timestamp).utc().locale('en').add(1, 'days').format('X');
        const diff = end - start;
        const secondsInDay = 86400;
        const readingsPercentageWidth = 65 / 702;
        const stackedReadingsRows = [];
        forEach(row, (rowData)=>{
            if (rowData.readingId || isManualReading(rowData)) {
                const rawReadingsRow = omit(rowData, 'relatedData');
                if (!Array.isArray(stackedReadingsRows[0])) {
                    stackedReadingsRows.push([
                        rawReadingsRow
                    ]);
                    return;
                }
                const possibleStack = find(stackedReadingsRows, (stackedRowsArray)=>(rawReadingsRow.timestamp - last(stackedRowsArray).timestamp) / secondsInDay > readingsPercentageWidth);
                if (possibleStack) {
                    possibleStack.push(rawReadingsRow);
                    return;
                }
                stackedReadingsRows.push([
                    rawReadingsRow
                ]);
            }
        });
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.day,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.day__container,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.day__wrapper,
                        children: [
                            this.renderBreakPoints(timestamp),
                            stackedReadingsRows.length ? map(stackedReadingsRows, (stackedRowData, idx)=>/*#__PURE__*/ _jsxs("div", {
                                    className: styles.row__wrapper,
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: styles.row__icon,
                                            children: /*#__PURE__*/ _jsx(AverageIcon, {})
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: styles.row,
                                            children: map(stackedRowData, (data)=>{
                                                const timeFromStart = data.timestamp - start;
                                                const percentageOnChart = (timeFromStart / diff * 100).toFixed(2);
                                                return this.renderReading(data, percentageOnChart);
                                            })
                                        })
                                    ]
                                }, idx)) : this.renderEmptyReadingsRow(),
                            this.renderRelatedDataRow(row, timestamp, 'foods'),
                            this.renderRelatedDataRow(row, timestamp, 'medications'),
                            this.renderRelatedDataRow(row, timestamp, 'activities')
                        ]
                    })
                }),
                this.renderSummary(row, timestamp, stackedReadingsRows)
            ]
        }, `day-${timestamp}`);
    }
    render() {
        const { rows } = this.state;
        return /*#__PURE__*/ _jsx("div", {
            className: cn(styles.wrapper, {
                'pt-4': this.props.showDayDividers
            }),
            children: map(rows, (row, timestamp)=>this.renderDay(row, timestamp))
        });
    }
    constructor(props){
        super(props);
        this.state = {
            rows: []
        };
    }
}
_define_property(DailyChart, "propTypes", {
    // Explicit props
    conversion: PropTypes.object.isRequired,
    standards: PropTypes.shape({
        maxValue: PropTypes.number.isRequired,
        preMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        }),
        postMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        })
    }),
    readings: PropTypes.array,
    relatedData: PropTypes.array,
    timeSeriesResources: PropTypes.array,
    phiSet: PropTypes.object,
    ticks: PropTypes.array,
    customRanges: PropTypes.array,
    hideEmptyRows: PropTypes.bool,
    showDayDividers: PropTypes.bool
});
export default withStyles(styles)(DailyChart);
